// Modal.tsx
import "./Modal.scss";
import { ReactNode, useEffect, useId, useRef } from "react";
import { displayCloseModalDialog } from "../../../services/Notifications/NotificationServices";
import { registerModal, unregisterModal, isTopModal } from "./modalManager";

declare type ModalClasses = {
  closeButton: string;
  modal: string;
}

export interface ModalComponentProps {
  closeModal: () => void;
  children?: ReactNode;
  isOpen: boolean;
  className?: string;
  classes?: Partial<ModalClasses>;
  closeAcception?: string | boolean;
}

const Modal = ({ children, closeModal, isOpen, className, classes, closeAcception }: ModalComponentProps) => {
  // Уникальный идентификатор для текущего экземпляра модального окна
  const modalId = useId()

  const handleOverlayClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) {
      if (!closeAcception) {
        closeModal();
      } else {
        displayCloseModalDialog(
          closeModal, 
          typeof closeAcception === "string" ? closeAcception : undefined
        );
      }
    }
  };

  // Управление регистрацией в стеке и блокировкой скролла
  useEffect(() => {
    if (isOpen) {
      registerModal(modalId);
    } else {
      unregisterModal(modalId);
    }

    // Очистка при размонтировании компонента (на случай принудительного удаления из DOM)
    return () => {
      unregisterModal(modalId);
    };
  }, [isOpen, modalId]);

  // Глобальный слушатель клавиши ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Закрываем только если это модальное окно открыто и оно является последним в стеке
      if (event.key === "Escape" && isOpen && isTopModal(modalId)) {
        if (!closeAcception) {
          closeModal();
        } else {
          displayCloseModalDialog(
            closeModal, 
            typeof closeAcception === "string" ? closeAcception : undefined
          );
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, modalId, closeModal, closeAcception]);

  // Формируем итоговый className без лишних пробелов
  const computedClassName = `modal ${className || ""} ${classes?.modal || ""}`.trim();

  return isOpen ? (
    <dialog className="overlay" onClick={handleOverlayClick}>
      <div className={computedClassName}>
        <img
          onClick={closeModal}
          src="/UI/closeModal.svg"
          alt="closeModal"
          className={`modal-close ${classes?.closeButton || ""}`}
        />
        {children}
      </div>
    </dialog>
  ) : null;
};

export default Modal;